import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { 
  ArrowLeft,
  Loader2,
  Zap,
  Target,
  Clock,
  BookOpen,
  CheckCircle2,
  AlertTriangle,
  TrendingUp,
  BarChart3,
  Sparkles,
  Play,
  Settings2,
  RefreshCw,
  Lock
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Brain logo
const BRAIN_LOGO = "https://customer-assets.emergentagent.com/job_e95fcea1-1d05-419a-a729-ac97ff58a502/artifacts/1u3r91fj_logo%20no%20back_Image_Jan_4__2026_at_02_55_25_PM-removebg-preview.png";

export default function PracticeExamGenerator() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isPremium, loading: authLoading, authChecked } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [config, setConfig] = useState(null);
  const [performanceSummary, setPerformanceSummary] = useState(null);
  
  // Exam configuration state
  const [numQuestions, setNumQuestions] = useState(50);
  const [timeLimit, setTimeLimit] = useState(60);
  const [focusWeakAreas, setFocusWeakAreas] = useState(true);
  const [selectedTaskLists, setSelectedTaskLists] = useState([]);
  const [difficultyMix, setDifficultyMix] = useState("balanced");

  useEffect(() => {
    if (authChecked && isAuthenticated && isPremium) {
      fetchConfigAndPerformance();
    }
  }, [authChecked, isAuthenticated, isPremium]);

  const fetchConfigAndPerformance = async () => {
    try {
      const token = localStorage.getItem("token");
      const [configRes, performanceRes] = await Promise.all([
        axios.get(`${API}/practice-exam/config`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/practice-exam/performance-summary`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setConfig(configRes.data);
      setPerformanceSummary(performanceRes.data);
      
      // Auto-select weak areas if available
      if (performanceRes.data.weak_areas?.length > 0) {
        setSelectedTaskLists(performanceRes.data.weak_areas.map(l => `task-list-${l.toLowerCase()}`));
      }
    } catch (error) {
      console.error("Failed to fetch config:", error);
      toast.error("Failed to load practice exam settings");
    } finally {
      setLoading(false);
    }
  };

  const generateExam = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/practice-exam/generate`, {
        num_questions: numQuestions,
        time_limit: timeLimit,
        focus_weak_areas: focusWeakAreas,
        include_task_lists: selectedTaskLists,
        difficulty_mix: difficultyMix
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Store the generated exam in sessionStorage
      sessionStorage.setItem("practiceExam", JSON.stringify(response.data));
      
      toast.success(`Generated ${response.data.question_count} question practice exam!`);
      
      // Navigate to quiz page with the practice exam
      navigate(`/quiz/${response.data.id}`);
    } catch (error) {
      console.error("Failed to generate exam:", error);
      toast.error(error.response?.data?.detail || "Failed to generate practice exam");
    } finally {
      setGenerating(false);
    }
  };

  const toggleTaskList = (taskListId) => {
    setSelectedTaskLists(prev => 
      prev.includes(taskListId)
        ? prev.filter(id => id !== taskListId)
        : [...prev, taskListId]
    );
  };

  const selectAllTaskLists = () => {
    if (config?.task_lists) {
      setSelectedTaskLists(config.task_lists.map(tl => tl.id));
    }
  };

  const selectWeakAreasOnly = () => {
    if (performanceSummary?.weak_areas) {
      setSelectedTaskLists(performanceSummary.weak_areas.map(l => `task-list-${l.toLowerCase()}`));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "mastered": return "text-green-600 bg-green-100";
      case "proficient": return "text-blue-600 bg-blue-100";
      case "learning": return "text-amber-600 bg-amber-100";
      case "needs_practice": return "text-red-600 bg-red-100";
      default: return "text-slate-600 bg-slate-100";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "mastered": return <CheckCircle2 className="w-4 h-4" />;
      case "proficient": return <TrendingUp className="w-4 h-4" />;
      case "learning": return <BookOpen className="w-4 h-4" />;
      case "needs_practice": return <AlertTriangle className="w-4 h-4" />;
      default: return null;
    }
  };

  // Auth loading state
  if (!authChecked || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50/30 via-white to-teal-50/20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // Not authenticated or not premium
  if (!isAuthenticated || !isPremium) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50/30 via-white to-teal-50/20 flex items-center justify-center p-6">
        <motion.div 
          className="max-w-md w-full text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-amber-100 p-8 shadow-lg">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-teal-100 flex items-center justify-center">
              <Lock className="w-8 h-8 text-teal-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">Premium Feature</h1>
            <p className="text-slate-600 mb-6">
              Subscribe to access the Practice Exam Generator and create custom exams tailored to your weak areas.
            </p>
            <Button 
              onClick={() => navigate("/auth")}
              className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6 font-semibold"
            >
              Subscribe Now - $7.00/month
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/quizzes")}
                className="text-slate-600 hover:text-teal-600"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl overflow-hidden">
                  <img src={BRAIN_LOGO} alt="Brain Logo" className="w-full h-full object-cover" />
                </div>
                <span className="font-bold text-xl tracking-tight text-slate-800 dark:text-slate-100">Practice Exam Generator</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Performance Summary */}
            <div className="lg:col-span-1 space-y-6">
              {/* Performance Overview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <h2 className="font-bold text-lg text-slate-900 dark:text-slate-100">Your Performance</h2>
                </div>
                
                {performanceSummary?.has_data ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-teal-600">{performanceSummary.overall_stats.overall_percentage}%</div>
                        <div className="text-xs text-slate-500">Overall Score</div>
                      </div>
                      <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-violet-600">{performanceSummary.overall_stats.total_attempts}</div>
                        <div className="text-xs text-slate-500">Quizzes Taken</div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                      <p className="text-sm text-amber-800 dark:text-amber-200">{performanceSummary.recommendation}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-slate-500 text-sm">Complete some quizzes to see your performance analysis</p>
                  </div>
                )}
              </motion.div>

              {/* Task List Performance */}
              {performanceSummary?.task_lists?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700"
                >
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-4">Task List Scores</h3>
                  <div className="space-y-3">
                    {performanceSummary.task_lists.map((tl) => (
                      <div key={tl.letter} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${getStatusColor(tl.status)}`}>
                            {tl.letter}
                          </span>
                          <span className="text-sm text-slate-600 dark:text-slate-400 truncate max-w-[140px]">
                            {tl.label.split(". ")[1]}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                tl.percentage >= 85 ? 'bg-green-500' : 
                                tl.percentage >= 70 ? 'bg-blue-500' : 
                                tl.percentage >= 50 ? 'bg-amber-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${tl.percentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300 w-10 text-right">
                            {tl.percentage}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Right Column - Exam Configuration */}
            <div className="lg:col-span-2 space-y-6">
              {/* Generator Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-teal-500 to-cyan-600 rounded-2xl p-6 text-white"
              >
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                    <Sparkles className="w-7 h-7" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">Custom Practice Exam</h1>
                    <p className="text-white/80">Generate a personalized exam weighted toward your weak areas</p>
                  </div>
                </div>
              </motion.div>

              {/* Configuration Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                    <Settings2 className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  </div>
                  <h2 className="font-bold text-lg text-slate-900 dark:text-slate-100">Exam Settings</h2>
                </div>

                <div className="space-y-6">
                  {/* Number of Questions */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                      Number of Questions: <span className="text-teal-600 font-bold">{numQuestions}</span>
                    </label>
                    <Slider
                      value={[numQuestions]}
                      onValueChange={(value) => setNumQuestions(value[0])}
                      max={150}
                      min={10}
                      step={5}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>10</span>
                      <span>150</span>
                    </div>
                  </div>

                  {/* Time Limit */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                      Time Limit: <span className="text-teal-600 font-bold">{timeLimit} minutes</span>
                    </label>
                    <Slider
                      value={[timeLimit]}
                      onValueChange={(value) => setTimeLimit(value[0])}
                      max={180}
                      min={15}
                      step={15}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>15 min</span>
                      <span>3 hours</span>
                    </div>
                  </div>

                  {/* Difficulty */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Difficulty Mix
                    </label>
                    <Select value={difficultyMix} onValueChange={setDifficultyMix}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select difficulty" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="balanced">Balanced (Mixed)</SelectItem>
                        <SelectItem value="easy">Easy Only</SelectItem>
                        <SelectItem value="medium">Medium Only</SelectItem>
                        <SelectItem value="hard">Hard Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Focus on Weak Areas Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Target className="w-5 h-5 text-amber-500" />
                      <div>
                        <div className="font-medium text-slate-900 dark:text-slate-100">Focus on Weak Areas</div>
                        <div className="text-sm text-slate-500">Weight questions toward areas needing practice</div>
                      </div>
                    </div>
                    <Switch
                      checked={focusWeakAreas}
                      onCheckedChange={setFocusWeakAreas}
                    />
                  </div>
                </div>
              </motion.div>

              {/* Task List Selection */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                    </div>
                    <div>
                      <h2 className="font-bold text-lg text-slate-900 dark:text-slate-100">Select Task Lists</h2>
                      <p className="text-sm text-slate-500">
                        {selectedTaskLists.length === 0 ? "All task lists will be included" : `${selectedTaskLists.length} selected`}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={selectAllTaskLists}
                      className="text-xs"
                    >
                      Select All
                    </Button>
                    {performanceSummary?.weak_areas?.length > 0 && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={selectWeakAreasOnly}
                        className="text-xs text-amber-600 border-amber-300 hover:bg-amber-50"
                      >
                        Weak Areas Only
                      </Button>
                    )}
                    {selectedTaskLists.length > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setSelectedTaskLists([])}
                        className="text-xs text-slate-500"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {config?.task_lists?.map((tl) => {
                    const isSelected = selectedTaskLists.includes(tl.id);
                    const isWeak = tl.needs_practice;
                    
                    return (
                      <button
                        key={tl.id}
                        onClick={() => toggleTaskList(tl.id)}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          isSelected 
                            ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' 
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <span className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${
                            isWeak ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {tl.label.charAt(0)}
                          </span>
                          {isSelected && (
                            <CheckCircle2 className="w-5 h-5 text-teal-500" />
                          )}
                        </div>
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                          {tl.label.split(". ")[1]}
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                          <span>{tl.question_count} questions</span>
                          {tl.average_score !== null && (
                            <>
                              <span>•</span>
                              <span className={isWeak ? 'text-amber-600' : 'text-green-600'}>
                                {tl.average_score}%
                              </span>
                            </>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </motion.div>

              {/* Generate Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Button
                  onClick={generateExam}
                  disabled={generating}
                  className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white py-6 text-lg font-semibold rounded-xl shadow-lg"
                  data-testid="generate-exam-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating Exam...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate {numQuestions}-Question Practice Exam
                    </>
                  )}
                </Button>
                
                <div className="flex items-center justify-center gap-4 mt-3 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {timeLimit} min
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Target className="w-4 h-4" />
                    {focusWeakAreas ? "Weighted" : "Random"}
                  </span>
                  <span>•</span>
                  <span className="capitalize">{difficultyMix}</span>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
